import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppMenuFilter from '@nxsys/components/AppMenuFilter';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import TaxYearSelect from '@nxsys/components/TaxYearSelect';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { reportActions } from '@nxsys/store/slices/report/reducer/reportSlice';
import { GetHolidayPayBalanceDetailListParams } from '@nxsys/store/slices/report/types';
import { Icons } from '@nxsys/types/icons';
import { getTaxWeekTaxYear, showCurrencyByCountry } from '@nxsys/utils';
import { debounce, find } from 'lodash';
import HolidayPayDetailFilterModal, {
  HolidayPayDetailFilterValues,
} from '../components/HolidayPayDetailFilterModal';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getHolidayPayBalanceDetailColumns } from './columns';
import { defaultPaymentFrequency } from '@nxsys/constants/AppConstants';
import { formatMoneyExportOrNull } from '@nxsys/utils/number-helper';
import moment from 'moment';
import { ExportToCsv } from 'export-to-csv';
import { showMessage } from '@nxsys/utils/message';
import { useGetHolidayPayList } from '@nxsys/hooks/api/holidayPay';

const HolidayPayDetailBalanceReport: React.FC = () => {
  const { messages } = useTypedIntl();
  const navigate = useNavigateWithQuery();

  const dispatch = useDispatch();
  const { isBureauEmployer, isBureau, user } = useSelector(
    (state: AppState) => state.appStore,
  );
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const { holidayPayBalanceDetailList } = useSelector(
    (state: AppState) => state.reportStore,
  );
  const { list, pagination, loading, queryParams } =
    holidayPayBalanceDetailList;
  const currentTaxYear = getTaxWeekTaxYear().taxYear;

  const [selectedRows, setSelectedRows] = useState<IHolidayPayBalance[]>([]);
  const [isOpenFilterModal, setIsOpenFilterModal] = useState(false);

  const { loading: loadingHolidayPayList, run: getHolidayPayList } =
    useGetHolidayPayList();

  const handleGoBack = useCallback(() => navigate('/reports'), [navigate]);

  const renderTitle = useCallback(() => {
    return (
      <div className="flex items-center gap-x-4">
        <AppBackButton onClick={handleGoBack} />
        <AppPageTitle
          prefix={messages['common.holidayPayBalanceDetail']}
          separator=""
          title=""
        />
      </div>
    );
  }, [messages, handleGoBack]);

  const renderRightContent = () => {
    return (
      <div className="flex items-center gap-x-4">
        <AppButton
          type="primary"
          name={messages['common.export']}
          leftIcon={<Icons.IconArrowExport />}
          variant="outlined"
          onClick={handleExportSelected}
        />
        <AppButton
          type="primary"
          name={messages['common.exportAll']}
          leftIcon={<Icons.IconArrowExport color="#fff" />}
          onClick={handleExportAll}
        />
      </div>
    );
  };

  const handleFetchData = useCallback(
    (params: GetHolidayPayBalanceDetailListParams) => {
      const newParams = {
        ...params,
        companyRefs: isBureauEmployer ? user?.companyUserIds : undefined,
      };

      dispatch(reportActions.getHolidayPayBalanceDetailList(newParams));
    },
    [dispatch, isBureauEmployer, user?.companyUserIds],
  );

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
      taxYear: currentTaxYear,
    });
  }, [currentTaxYear]);

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearch = useCallback(
    debounce(
      (e: React.ChangeEvent<HTMLInputElement>) =>
        handleFetchData({ ...queryParams, search: e.target.value }),
      500,
    ),
    [queryParams],
  );

  const handleFilter = (values: HolidayPayDetailFilterValues) => {
    const params: GetHolidayPayBalanceDetailListParams = {
      ...queryParams,
      taxPeriodFrom: values.taxPeriodFrom,
      taxPeriodTo: values.taxPeriodTo,
      agencyId: values.agency?.value,
      payFrequency: values.payFrequency,
    };
    handleFetchData(params);
  };

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleFetchData({
        ...queryParams,
        sortField: sorter.field ?? '',
        sortType: sorter.order,
      });
    } else {
      handleFetchData({
        ...queryParams,
        sortField: undefined,
        sortType: undefined,
      });
    }
  };

  const handleExport = (data: IHolidayPayBalance[]) => {
    const dataExport = data.map((item) => {
      const {
        subcontractor,
        payroll,
        openingBalance,
        openingERNI,
        openingAppLevy,
        openingERPension,
        accrued,
        paid,
        balance,
        ERNI,
        appLevy,
        ERPension,
      } = item;
      const { primaryAgency, agencyRef, fullName, forename, surname } =
        subcontractor;
      const effectivePayFrequency =
        payroll?.payFrequency || subcontractor?.payFrequency;
      const commonRow = {
        agencyRef: isBureau
          ? agencyRef
          : `${(subcontractor as any)?.departmentPrefix || primaryAgency?.agencyPrefix || '___'}/${agencyRef || ''}`,
        employeeName: fullName || `${forename || ''} ${surname || ''}` || '',
        ...(isBureau ? {} : { agencyName: payroll?.agencyRef?.name || '' }),
        taxPeriod: payroll?.taxPeriod || '',
        taxYear:
          payroll?.taxYear ||
          payroll?.selfBillRef?.taxYear ||
          item.taxYear ||
          '',
        payFrequency: effectivePayFrequency
          ? find(
              defaultPaymentFrequency,
              (i) => i?.value === effectivePayFrequency,
            )?.label
          : '',
        employer:
          payroll?.companyRef?.companyName ||
          subcontractor?.companyPayment?.companyName ||
          '',
      };
      if (isBureau) {
        return {
          ...commonRow,
          openingBalance: formatMoneyExportOrNull(
            openingBalance,
            showCurrencyByCountry(payrollCountry),
          ),
          accrued: formatMoneyExportOrNull(
            accrued,
            showCurrencyByCountry(payrollCountry),
          ),
          paid: formatMoneyExportOrNull(paid),
          balance: formatMoneyExportOrNull(balance),
        };
      }
      return {
        ...commonRow,
        openingBalance: formatMoneyExportOrNull(
          openingBalance,
          showCurrencyByCountry(payrollCountry),
        ),
        openingERNI: formatMoneyExportOrNull(
          openingERNI,
          showCurrencyByCountry(payrollCountry),
        ),
        openingAppLevy: formatMoneyExportOrNull(
          openingAppLevy,
          showCurrencyByCountry(payrollCountry),
        ),
        openingERPension: formatMoneyExportOrNull(
          openingERPension,
          showCurrencyByCountry(payrollCountry),
        ),
        accrued: formatMoneyExportOrNull(
          accrued,
          showCurrencyByCountry(payrollCountry),
        ),
        retainedHolidayPayERNI: formatMoneyExportOrNull(
          payroll?.payeeUmbrellaValue?.retainedHolidayPayERNI,
          showCurrencyByCountry(payrollCountry),
        ),
        retainedHolidayPayAppLevy: formatMoneyExportOrNull(
          payroll?.payeeUmbrellaValue?.retainedHolidayPayAppLevy,
          showCurrencyByCountry(payrollCountry),
        ),
        retainedHolidayPayErPen: formatMoneyExportOrNull(
          payroll?.payeeUmbrellaValue?.retainedHolidayPayErPen,
          showCurrencyByCountry(payrollCountry),
        ),
        paid: formatMoneyExportOrNull(paid),
        retainedHPPaidOutERNI: formatMoneyExportOrNull(
          payroll?.payeeUmbrellaValue?.retainedHPPaidOutERNI,
          showCurrencyByCountry(payrollCountry),
        ),
        hpRetainedPaidOutAppLevy: formatMoneyExportOrNull(
          payroll?.payeeUmbrellaValue?.hpRetainedPaidOutAppLevy,
          showCurrencyByCountry(payrollCountry),
        ),
        hpRetainedPaidOutErPen: formatMoneyExportOrNull(
          payroll?.payeeUmbrellaValue?.hpRetainedPaidOutErPen,
          showCurrencyByCountry(payrollCountry),
        ),
        balance: formatMoneyExportOrNull(balance),
        ERNI: formatMoneyExportOrNull(ERNI),
        appLevy: formatMoneyExportOrNull(appLevy),
        ERPension: formatMoneyExportOrNull(ERPension),
      };
    });

    const headers = [
      isBureau ? 'Employer Ref' : 'Agency Ref',
      'Employee Name',
      ...(isBureau ? [] : ['Agency Name']),
      'Tax Period',
      'Tax Year',
      'Pay Frequency',
      isBureau ? 'Employer' : 'Payment Company',
      ...(isBureau
        ? ['Opening Balance', 'Accrued', 'Paid', 'Closing Balance']
        : [
            'Holiday Pay(Opening)',
            'ERNI(Opening)',
            'App Levy(Opening)',
            'Empr Pension(Opening)',
            'Holiday Pay(Accrued)',
            'ERNI(Accrued)',
            'App Levy(Accrued)',
            'Empr Pension(Accrued)',
            'Holiday Pay(Paid)',
            'ERNI(Paid)',
            'App Levy(Paid)',
            'Empr Pension(Paid)',
            'Holiday Pay(Closing)',
            'ERNI(Closing)',
            'App Levy(Closing)',
            'Empr Pension(Closing)',
          ]),
    ];

    const csvExporter = new ExportToCsv({
      filename: `Holiday-pay-report-${moment(new Date())
        .tz('Europe/London')
        .format('DD/MM/YYYY')}`,
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      useTextFile: false,
      useBom: true,
      headers,
    });
    try {
      csvExporter.generateCsv(dataExport);
    } catch (e) {
      console.error('Export failed:', e);
      console.error(e);
    }
  };

  const handleExportSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleExport(selectedRows);
  };

  const handleExportAll = () => {
    if (!pagination.totalDocs) {
      showMessage({
        type: 'warning',
        content: messages['common.noDataToExport'],
      });
      return;
    }
    getHolidayPayList({
      ...queryParams,
      page: 0,
      size: pagination.totalDocs,
    }).then((res) => {
      handleExport(res.data);
    });
  };

  const isFiltering =
    !!queryParams?.search ||
    !!queryParams?.taxPeriodFrom ||
    !!queryParams?.taxPeriodTo ||
    !!queryParams?.agencyId ||
    !!queryParams?.payFrequency;

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={loadingHolidayPayList}
    >
      <AppCard>
        <AppsHeader>
          <div className="flex justify-between flex-wrap w-full gap-4">
            <div className="flex flex-wrap gap-4">
              <AppInputSearch onChange={handleSearch} />
              <AppMenuFilter
                options={[]}
                onChange={() => {}}
                onClick={() => setIsOpenFilterModal(true)}
              />
            </div>
            <div className="flex flex-wrap gap-4 items-end">
              <TaxYearSelect
                initValue={currentTaxYear}
                onFilter={(value) =>
                  handleFetchData({ ...queryParams, taxYear: value.taxYear })
                }
              />
            </div>
          </div>
        </AppsHeader>
        <AppsContent>
          <AppTableContainer
            rowKey="id"
            data={list ?? []}
            loading={loading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            scroll={{ x: isBureau ? 1400 : 2000, y: 700 }}
            columns={getHolidayPayBalanceDetailColumns({
              messages,
              payrollCountry,
              isBureau,
            })}
            onChangePagination={onChangePagination}
            rowSelection={{
              type: 'checkbox',
              onChange: (_: React.Key[], selectedRows: any[]) => {
                setSelectedRows(selectedRows);
              },
            }}
            onChangeTable={onChangeTable}
            noDataType={isFiltering ? 'noDataFound' : 'noDataToShow'}
          />
        </AppsContent>
      </AppCard>
      <HolidayPayDetailFilterModal
        isOpen={isOpenFilterModal}
        toggle={() => setIsOpenFilterModal(false)}
        onFilter={handleFilter}
        isBureau={isBureau}
      />
    </AppsContainer>
  );
};

export default HolidayPayDetailBalanceReport;
